import { useCallback, useEffect, useRef, useState } from 'react';
import {
  acceptSaenggibuSuggestion,
  dismissSaenggibuSuggestion,
  getSaenggibuSuggestion,
} from '../api/saenggibuApi';
import { getSaenggibuSuggestionFingerprint } from '../utils/saenggibuSuggestion';
import { trackSaenggibuUxEvent } from '../utils/eventTracker';

const trackSuggestion = (action, suggestionType, { category, isBatch }) => {
  trackSaenggibuUxEvent(action, {
    suggestion_type: suggestionType,
    saenggibu_category: category,
    is_batch: isBatch,
  });
};

const useSaenggibuSuggestion = ({
  recordId,
  category,
  isBatch = false,
  enabled = true,
} = {}) => {
  const [suggestion, setSuggestion] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const fetchedKeyRef = useRef('');
  const suggestionRef = useRef(null);
  const resultRef = useRef(null);

  suggestionRef.current = suggestion;
  resultRef.current = result;

  const load = useCallback(async (flags = {}) => {
    if (!enabled || !recordId) {
      setSuggestion(null);
      return null;
    }
    try {
      const data = await getSaenggibuSuggestion({
        recordId,
        category,
        isBatch,
        copied: Boolean(flags.copied),
        edited: Boolean(flags.edited),
        versionIndex: flags.versionIndex,
      });
      const next = data?.suggestion || null;
      setSuggestion(next);
      setResult(null);
      if (next) {
        trackSuggestion('saenggibu_suggestion_shown', next.type, { category, isBatch });
      }
      return next;
    } catch (_error) {
      setSuggestion(null);
      setResult(null);
      return null;
    }
  }, [recordId, category, isBatch, enabled]);

  useEffect(() => {
    if (!enabled || !recordId) {
      setSuggestion(null);
      setResult(null);
      fetchedKeyRef.current = '';
      return undefined;
    }
    const key = `${recordId}|${category || ''}|${isBatch ? 1 : 0}`;
    if (fetchedKeyRef.current === key) return undefined;
    fetchedKeyRef.current = key;
    load();
    return undefined;
  }, [recordId, category, isBatch, enabled, load]);

  const refetch = useCallback(async (flags = {}) => {
    if (!enabled || !recordId) return null;
    if (suggestionRef.current && !resultRef.current) return suggestionRef.current;
    return load(flags);
  }, [enabled, recordId, load]);

  const dismiss = useCallback(async () => {
    const current = suggestionRef.current;
    if (!current || busy) return null;
    setBusy(true);
    try {
      await dismissSaenggibuSuggestion({
        fingerprint: getSaenggibuSuggestionFingerprint(current),
        type: current.type,
      });
    } catch (_error) {
      // 거절 실패도 카드를 접는다 (서버 never-repeat와 무관하게 UX는 fail-open).
    } finally {
      setBusy(false);
    }
    setResult({ kind: 'dismissed' });
    setSuggestion(null);
    trackSuggestion('saenggibu_suggestion_dismissed', current.type, { category, isBatch });
    return { dismissed: true };
  }, [busy, category, isBatch]);

  const act = useCallback(async (action, extras = {}) => {
    const current = suggestionRef.current;
    if (!current || busy) return null;
    if (action === 'dismiss') {
      return dismiss();
    }
    setBusy(true);
    try {
      const data = await acceptSaenggibuSuggestion({
        fingerprint: getSaenggibuSuggestionFingerprint(current),
        type: current.type,
        payload: current.payload || {},
        option: extras.option,
        text: extras.text,
      });
      setResult(data?.result || { kind: 'accepted' });
      trackSuggestion('saenggibu_suggestion_accepted', current.type, { category, isBatch });
      return data;
    } catch (error) {
      // 404 = 기록을 못 찾음(record_not_found) 또는 플래그 off → 카드를 조용히 접는다 (토스트 없음).
      // 400/403 은 사용자가 고칠 수 있는 안내(detail)이므로 호출측(runner)이 토스트로 보여준다.
      if (error?.status === 404) {
        setResult({ kind: 'unavailable' });
        setSuggestion(null);
        return null;
      }
      throw error;
    } finally {
      setBusy(false);
    }
  }, [busy, category, dismiss, isBatch]);

  return {
    suggestion,
    result,
    busy,
    refetch,
    act,
    dismiss,
  };
};

export default useSaenggibuSuggestion;
